import { PtModeEnum, TripTypeEnum } from "../../Orchestrator/Types/Enums/globalDefaultEnum";
import { dubaiInstant } from "./shuttleTime";
import { isInbound, filterBookingByOfferId } from "./validationUtil";
import { getRecoilStateValue, updateRecoilState } from "../../Orchestrator/Recoils/Events/Recoil";
import { IBooking, IBookingResponse, IBookingTypeTrip, ILeg, ILegTrain, ISelectedTrainLegs } from "../../Orchestrator/Types/Index";
import { IExchangeOperations } from "../../Orchestrator/Types/Interface/IBooking";

/**
 * Recovers trip directions when the backend re-labels them after POST /replace.
 *
 * Observed on roundtrip upsell: a /replace can flip a trip's `direction` field
 * such that `OUT_BOUND`/`IN_BOUND` no longer matches the trip's actual OD.
 * Anywhere downstream that filters trips by `trip.direction === OUT_BOUND` or
 * `=== IN_BOUND` (CheckoutPage segment indexing, GetPassengerListByOffer-
 * AdmissionsAction's per-direction passenger split, PrepareBookingSummary's
 * leg summary, the per-direction exchange-fee split) reads the wrong leg and
 * the payment summary loses a row or attributes the wrong fare to a direction.
 *
 * Two failure modes are recovered:
 *  - Collapse: both trips end up tagged with the same direction.
 *  - Swap: each trip's direction is swapped with the other's.
 *
 * Recovery strategy: compare each trip's overall start/end stop refs (first
 * leg's `start` + last leg's `end`) against the LAST known
 * `selectedTrainLegsAtom`'s outbound AND inbound trip-level endpoints. Each
 * trip is re-tagged independently based on which anchor it matches — no
 * blanket "flip everything non-matched" pass. Trips that match the inbound
 * anchor are tagged IN_BOUND; trips that match the outbound anchor are tagged
 * OUT_BOUND. The matcher always runs when at least one anchor is available;
 * the only no-op exits are guards for invalid/missing inputs.
 *
 * Trip IDs persist across /replace, but legIds change. Stop refs are the
 * stable signal — every trip on a given booking has the same OD pair through
 * its lifetime regardless of which offer covers it.
 *
 * No-ops when:
 *   - fewer than 2 trips
 *   - both `selectedTrainLegs.outbound` and `selectedTrainLegs.inbound` are
 *     missing (one-way booking, or no prior state)
 *   - the inbound and outbound anchors are field-equal (symmetric same-OD
 *     itineraries) — recovery cannot disambiguate which direction is which
 *   - no trip matches either anchor
 */

interface DirectionAnchor {
  start: string;
  end: string;
}

const tripOverallStops = (trip: IBookingTypeTrip): DirectionAnchor | null => {
  const legs = trip?.legs || [];
  if (legs.length === 0) return null;
  const start = legs[0]?.timedLeg?.start?.stopPlaceRef?.stopPlaceRef;
  const end = legs[legs.length - 1]?.timedLeg?.end?.stopPlaceRef?.stopPlaceRef;
  if (!start || !end) return null;
  return { start, end };
};

const legArrayOverallStops = (legs: ILegTrain[] | undefined): DirectionAnchor | null => {
  if (!legs || legs.length === 0) return null;
  const start = legs[0]?.fromName;
  const end = legs[legs.length - 1]?.toName;
  if (!start || !end) return null;
  return { start, end };
};

const sameAnchor = (a: DirectionAnchor | null, b: DirectionAnchor | null): boolean => {
  if (!a || !b) return false;
  return a.start === b.start && a.end === b.end;
};

export function recoverTripDirections(
  trips: IBookingTypeTrip[],
  priorSelectedTrainLegs: ISelectedTrainLegs | null | undefined,
): IBookingTypeTrip[] {
  if (!trips || trips.length < 2) return trips;

  const inboundAnchor = legArrayOverallStops(priorSelectedTrainLegs?.inbound);
  const outboundAnchor = legArrayOverallStops(priorSelectedTrainLegs?.outbound);
  if (!inboundAnchor && !outboundAnchor) return trips;

  // Anchors with identical {start,end} can't be used to disambiguate. Bail
  // out so we don't mis-classify (e.g. flip a correctly-tagged IN_BOUND trip
  // to OUT_BOUND just because both anchors happen to point at the same
  // stop pair on a symmetric itinerary or stale state where outbound was
  // copy-written into the inbound slot).
  if (inboundAnchor && outboundAnchor && sameAnchor(inboundAnchor, outboundAnchor)) {
    return trips;
  }

  let mutated = false;
  const next = trips.map((trip) => {
    const tripStops = tripOverallStops(trip);
    if (!tripStops) return trip;
    // Use STRICT equality against the canonical TripTypeEnum value (not
    // `isInbound`/`isOutbound`). Those helpers also accept Transcity's
    // alternate format (`"INBOUND"` / `"OUTBOUND"` without underscore) —
    // skipping the rewrite for an alt-format match would leave the booking
    // carrying a non-canonical tag that several downstream consumers
    // (e.g. PrepareBookingSummaryAction's per-direction loop uses
    // `trip.direction === TripTypeEnum.OUT_BOUND`) compare strictly. Always
    // normalize to the canonical value when an anchor matches.
    if (sameAnchor(tripStops, inboundAnchor)) {
      if (trip?.direction === TripTypeEnum.IN_BOUND) return trip;
      mutated = true;
      return { ...trip, direction: TripTypeEnum.IN_BOUND } as IBookingTypeTrip;
    }
    if (sameAnchor(tripStops, outboundAnchor)) {
      if (trip?.direction === TripTypeEnum.OUT_BOUND) return trip;
      mutated = true;
      return { ...trip, direction: TripTypeEnum.OUT_BOUND } as IBookingTypeTrip;
    }
    return trip;
  });
  return mutated ? next : trips;
}

const isTrainLeg = (leg: ILeg | undefined): boolean =>
  leg?.timedLeg?.service?.mode?.ptMode === PtModeEnum.TRAIN;

const legStartRef = (leg: ILeg | undefined): string =>
  leg?.timedLeg?.start?.stopPlaceRef?.stopPlaceRef || "";
const legEndRef = (leg: ILeg | undefined): string =>
  leg?.timedLeg?.end?.stopPlaceRef?.stopPlaceRef || "";
const legDeparture = (leg: ILeg | undefined): number =>
  dubaiInstant(leg?.timedLeg?.start?.serviceDeparture?.timetabledTime);
const legArrival = (leg: ILeg | undefined): number =>
  dubaiInstant(leg?.timedLeg?.end?.serviceArrival?.timetabledTime);

/**
 * The canonical enum value for a direction string.
 *
 * Same reason recoverTripDirections normalizes above: Transcity spells these
 * "INBOUND"/"OUTBOUND" while the Retailer API spells them "IN_BOUND"/
 * "OUT_BOUND", and consumers compare STRICTLY (PrepareBookingSummaryAction's
 * per-direction loop is `=== TripTypeEnum.OUT_BOUND`). Copying a train's raw
 * spelling onto a shuttle would hand those consumers a value they cannot match,
 * which — for a shuttle that used to carry the canonical spelling of its own
 * bogus stamp — would move it to the WRONG bucket rather than the right one.
 *
 * Spelling knowledge is `isInbound`'s, not ours: a third spelling should have
 * one place to land.
 */
export const canonicalDirection = (
  direction: string | undefined
): TripTypeEnum.OUT_BOUND | TripTypeEnum.IN_BOUND =>
  isInbound(direction) ? TripTypeEnum.IN_BOUND : TripTypeEnum.OUT_BOUND;

/**
 * The direction of an ONWARD (train-less) trip — a shuttle booked to or from a
 * station — read from the train it connects to instead of from its own
 * `direction` field.
 *
 * The backend stamps EVERY shuttle trip OUT_BOUND (measured on preprod
 * 2026-08-11: a shuttle added for the INBOUND leg's arrival came back
 * `direction: OUT_BOUND`), so on a round trip the payment summary printed the
 * return leg's shuttle under the outbound heading — the customer reads a
 * shuttle they booked for the way home as part of the way out.
 *
 * A shuttle connects to the train whose station it shares: it either ENDS where
 * a train STARTS ("travel to the station") or STARTS where a train ENDS ("reach
 * your final destination"). Those two rules are direction-asymmetric, so on a
 * plain round trip the STATION alone already decides — a shuttle arriving at
 * Abu Dhabi can only be continuing from the train that ends there.
 *
 * The time comparison is for where it genuinely cannot: an INTERMEDIATE station
 * of a connecting journey is both an end and a start, and both directions pass
 * through it, so a shuttle meeting the passenger there matches twice. The
 * closest connection in time wins, which is the one the passenger booked. Two
 * trips of the same direction on a multibooking match twice as well; they agree
 * on the answer, so the tie-break only has to not crash.
 *
 * That ambiguous case cannot be CREATED on this web checkout — OnwardTravelCard
 * only sells stops at the leg's own origin/destination — but the summary is
 * built from whatever the booking holds, including bookings made on mobile or
 * through the retired end-to-end drawer. Do not read the tie-break as dead code
 * because the web UI cannot reach it.
 *
 * EVERY train leg's endpoints are considered, not just the trip's outer pair —
 * that intermediate station is a real case (see the stationRefs note in
 * PrepareBookingSummaryAction), and reading only the outer pair left an inbound
 * intermediate shuttle on the outbound's bogus stamp.
 *
 * Falls back to the trip's own direction for train trips, for a shuttle sharing
 * no station with any train, and for a booking whose times are unreadable
 * (a station match with no usable time still beats no match at all, so it is
 * ranked last rather than dropped).
 */
export function onwardTripDirection(
  trip: IBookingTypeTrip | undefined,
  trips: IBookingTypeTrip[] | undefined,
): TripTypeEnum.OUT_BOUND | TripTypeEnum.IN_BOUND | undefined {
  const legs = trip?.legs || [];
  // A train trip states its own direction; only the train-less onward trips
  // carry the bogus field.
  //
  // Deliberately NOT canonicalised, unlike the matched direction below. The
  // asymmetry looks wrong and is not: canonicalDirection answers OUT_BOUND for
  // anything that is not inbound, including `undefined`, so applying it here
  // would turn "this trip has no direction" — a shape the caller must be free
  // to treat as unknown — into a confident outbound. A train carrying the
  // Transcity spelling is already normalised upstream by
  // recoverBookingTripDirections; this function has no business inventing one.
  if (legs.length === 0 || legs.some(isTrainLeg)) return trip?.direction;

  const shuttleFrom = legStartRef(legs[0]);
  const shuttleTo = legEndRef(legs[legs.length - 1]);
  const shuttleDeparture = legDeparture(legs[0]);
  const shuttleArrival = legArrival(legs[legs.length - 1]);

  let bestDirection: TripTypeEnum.OUT_BOUND | TripTypeEnum.IN_BOUND | undefined;
  let bestGap = Number.POSITIVE_INFINITY;
  const consider = (candidate: IBookingTypeTrip, gap: number) => {
    // NaN (an unparseable timetable) must not win a comparison, but the station
    // match it came with is still evidence — rank it below every timed match.
    const ranked = Number.isNaN(gap) ? Number.MAX_SAFE_INTEGER : gap;
    if (ranked >= bestGap) return;
    bestGap = ranked;
    bestDirection = canonicalDirection(candidate.direction);
  };

  (trips || []).forEach((candidate) => {
    (candidate?.legs || []).filter(isTrainLeg).forEach((trainLeg) => {
      // Shuttle ends where this train leg begins — it feeds that departure.
      if (shuttleTo && shuttleTo === legStartRef(trainLeg)) {
        consider(candidate, Math.abs(legDeparture(trainLeg) - shuttleArrival));
      }
      // Shuttle begins where this train leg ends — it continues from that arrival.
      if (shuttleFrom && shuttleFrom === legEndRef(trainLeg)) {
        consider(candidate, Math.abs(shuttleDeparture - legArrival(trainLeg)));
      }
    });
  });

  return bestDirection ?? trip?.direction;
}

/**
 * Which leg does this onward SHUTTLE trip belong to?
 *
 * `trip.direction` cannot answer it: the backend stamps EVERY onward shuttle
 * OUT_BOUND, including the one that runs after the inbound train lands. So a
 * shuttle must be matched to the TRAIN it connects to — which is exactly what
 * `onwardTripDirection` already does, station-first with the time gap only
 * ranking rival matches.
 *
 * Delegated rather than reimplemented, and that matters: a hand-rolled
 * "connects, AND the timing agrees" predicate DROPS real bookings. Live data
 * carries zero-duration shuttle trips (62KQNE's return shuttle is stamped
 * 05:00 -> 05:00, an hour before the inbound train it serves even arrives), and
 * a rule that treats time as disqualifying rather than merely ranking loses that
 * shuttle from the summary entirely — understating both Sub Total and the
 * refund. Station match decides; time only breaks ties.
 */
export function shuttleServesDirection(
  shuttleTrip: IBookingTypeTrip | undefined,
  allTrips: IBookingTypeTrip[] | undefined,
  // Typed, not `string`: a stray spelling would just return false and silently
  // drop the shuttle. NOTE only the MATCHED direction is canonicalised —
  // `onwardTripDirection` hands back `trip.direction` VERBATIM for train trips
  // and for the no-match fallback, so a caller deriving an anchor from it must
  // run it through `canonicalDirection` first or an "OUTBOUND"-spelled train
  // will match nothing and silently drop every shuttle.
  direction: TripTypeEnum.OUT_BOUND | TripTypeEnum.IN_BOUND,
): boolean {
  const recovered = onwardTripDirection(shuttleTrip, allTrips || []);
  return !!recovered && recovered === direction;
}

/**
 * Booking-level convenience wrapper. Returns a shallow-cloned booking with
 * `trips` AND `exchangeOperations[].trips` recovered when any direction was
 * corrected; otherwise returns the input unchanged so reference equality
 * (and downstream useEffect deps) stays stable on no-op runs.
 *
 * Patches `exchangeOperations[].trips` because preparePaymentSummary's
 * exchange-fee + alreadyPaid split iterates those trips with `isInbound`/
 * `isOutbound` filters — a separate read path from `booking.trips`.
 */
export function recoverBookingTripDirections(
  booking: IBooking | null | undefined,
  priorSelectedTrainLegs: ISelectedTrainLegs | null | undefined,
): IBooking | null | undefined {
  if (!booking) return booking;

  const recoveredTrips = booking.trips
    ? recoverTripDirections(booking.trips, priorSelectedTrainLegs)
    : booking.trips;

  // Recover exchangeOperations[].trips only when something actually changed.
  // We short-circuit the .map allocation on the common no-op path so atom
  // re-renders aren't triggered by allocation churn.
  const ops = booking.exchangeOperations as IExchangeOperations[] | undefined;
  let recoveredOps: IExchangeOperations[] | undefined = ops;
  let opsMutated = false;
  if (Array.isArray(ops) && ops.length > 0) {
    for (let i = 0; i < ops.length; i++) {
      const op = ops[i];
      const opTrips = op?.trips as IBookingTypeTrip[] | undefined;
      if (!opTrips || opTrips.length < 2) continue;
      const recovered = recoverTripDirections(opTrips, priorSelectedTrainLegs);
      if (recovered === opTrips) continue;
      if (!opsMutated) {
        recoveredOps = ops.slice();
        opsMutated = true;
      }
      recoveredOps![i] = { ...op, trips: recovered };
    }
  }

  const tripsChanged = recoveredTrips !== booking.trips;
  if (!tripsChanged && !opsMutated) return booking;

  const next: IBooking = { ...booking };
  if (tripsChanged) next.trips = recoveredTrips;
  if (opsMutated) next.exchangeOperations = recoveredOps;
  return next;
}

/**
 * Response-level convenience wrapper. Call sites that receive a fresh
 * IBookingResponse from `getBooking` (or `replaceBookedOffer`, etc.) and are
 * about to write it into `bookingResponseAtom` should route through
 * {@link storeBookingResponseAtom} which uses this internally.
 */
export function recoverBookingResponseDirections(
  response: IBookingResponse | null | undefined,
  priorSelectedTrainLegs: ISelectedTrainLegs | null | undefined,
): IBookingResponse | null | undefined {
  if (!response?.booking) return response;
  const recoveredBooking = recoverBookingTripDirections(response.booking, priorSelectedTrainLegs);
  if (recoveredBooking === response.booking) return response;
  return { ...response, booking: recoveredBooking as IBooking } as IBookingResponse;
}

/**
 * Atomic recover + `updateRecoilState("bookingResponseAtom", ...)` helper.
 *
 * Sites that store a freshly-fetched IBookingResponse into the atom and run
 * AFTER an upsell (`/replace`) should call this instead of a bare
 * `updateRecoilState` so the atom never carries a direction-flipped booking
 * that downstream consumers (`MultiBookingSeatingSummary`,
 * `CheckoutTripSummary`, every `booking.trips[].direction` reader) would
 * mis-render.
 *
 * In-scope callers (apply the helper):
 *   - `ApplyCheckoutUpsellAction`
 *   - `ConfirmSeatSelectionAction`
 *   - `PersistAddonsOnConfirmAction` (main + remove-all + rollback)
 *   - the onward-travel controllers (`confirmShuttle`, `removeShuttle`,
 *     `confirmNol`, `confirmOnwardSelection`, `quoteOnwardRemoval`,
 *     `confirmOnwardRemoval`) — added for the direction recovery like the rest,
 *     but ALSO because they previously wrote the atom with a bare
 *     `appStore.set`, which never reaches localStorage / `bufferMap` /
 *     `atomValueCache`. See `updateRecoilState` for why that matters.
 *
 * Out-of-scope (NOT routed through the helper by design): voucher actions,
 * upgrade flows, change-date / quick-exchange, return-journey, multibooking
 * prebook, booking-details. Those writers exist on paths that do not run
 * /replace as part of THIS branch's fix scope, and they already go through
 * `updateRecoilState`, so they carry no bare-`appStore.set` divergence. If a
 * future bug surfaces on those flows from the same gateway behavior, route
 * them through this helper at that time.
 *
 * Returns the response that was actually written to the atom so callers can
 * chain further work without re-reading. When `priorSelectedTrainLegs` is
 * provided (callers should snapshot it BEFORE awaits — `selectedTrainLegsAtom`
 * is rewritten by `prepareBookingSummaryAction → updateLegTripSummary` so
 * post-await reads can race), uses it as the recovery signal; otherwise reads
 * the atom at call time.
 */
/**
 * Re-narrow a freshly read booking to the scope the active flow set.
 *
 * Add-return-trip scopes bookingResponseAtom to the new offers so its checkout
 * prices only what is unpaid. Every End-to-end add — Nol and shuttle alike —
 * ends by re-reading the WHOLE booking through this funnel, which silently
 * widened it again: the already-paid outbound came back into the summary and
 * into the total, so a 64 AED checkout asked for 124 (INT booking 7QYI45).
 *
 * The scope carries the booking it was taken from and is dropped on any other
 * booking, so a stale one cannot bleed across bookings the way the persisted
 * isQuickExchangeStateAtom did. It is not persisted, so a reload clears it.
 *
 * Returns the response untouched when no scope is set — every other surface
 * (booking details, cancel, MSA) keeps seeing the full booking.
 */
function applyBookingScope(response: IBookingResponse): IBookingResponse {
  const scope = getRecoilStateValue<{ bookingId: string; offerIds: string[] } | null>(
    "bookingScopeAtom"
  );
  if (!scope || !scope.offerIds?.length) return response;
  if (scope.bookingId !== response?.booking?.id) {
    // A different booking — the scope belongs to a flow that is no longer live.
    updateRecoilState("bookingScopeAtom", null);
    return response;
  }
  return filterBookingByOfferId(response, scope.offerIds);
}

export function storeBookingResponseAtom(
  response: IBookingResponse | null | undefined,
  priorSelectedTrainLegs?: ISelectedTrainLegs | null,
): IBookingResponse | null | undefined {
  if (!response) {
    updateRecoilState("bookingResponseAtom", response);
    return response;
  }
  const anchor = priorSelectedTrainLegs !== undefined
    ? priorSelectedTrainLegs
    : getRecoilStateValue<ISelectedTrainLegs>("selectedTrainLegsAtom");
  const recovered = recoverBookingResponseDirections(response, anchor) ?? response;
  updateRecoilState("bookingResponseAtom", applyBookingScope(recovered));
  return recovered;
}
